import React, { useState } from "react";
import { Button } from "./ui/Button";
import { Input } from "./ui/input2";
import { X, Clock, Info } from "lucide-react";
import TimePickerModal from "./TimePickerModal";
import FormField from "./ui/FormField";
import SectionHeader from "./ui/SectionHeader";

interface ModalData {
  openTime: string;
  closeTime: string;
  slotDuration: string;
  allowMultipleBookings: string;
  maxBookingsAllowed: string;
  maxBookingsPerDay: string;
  maxBookingsPerSlot: string;
  status: string;
}

interface EditDayScheduleModalProps {
  isOpen: boolean;
  dayName: string;
  modalData: ModalData;
  onClose: () => void;
  onSubmit: () => void;
  onInputChange: (field: string, value: string) => void;
}

const EditDayScheduleModal: React.FC<EditDayScheduleModalProps> = ({
  isOpen,
  dayName,
  modalData,
  onClose,
  onSubmit,
  onInputChange,
}) => {
  const [isOpenTimeModalOpen, setIsOpenTimeModalOpen] = useState(false);
  const [isCloseTimeModalOpen, setIsCloseTimeModalOpen] = useState(false);

  if (!isOpen) return null;

  return (
    <>
      {/* Modal Overlay */}
      <div className="fixed inset-0  backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
        <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full my-8 animate-slide-up">
          {/* Modal Header */}
          <div className="bg-[#885ABB] px-6 py-4 rounded-t-xl flex items-center justify-between">
            <h3 className="text-xl font-semibold text-white flex items-center gap-2">
              <Clock size={20} />
              Edit Schedule - {dayName}
            </h3>
            <button
              onClick={onClose}
              className="text-white hover:bg-white/20 rounded-full p-1 transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Modal Body */}
          <div className="p-6 space-y-5 max-h-[calc(100vh-250px)] overflow-y-auto">
            {/* Time Settings Section */}
            <div>
              <SectionHeader title="Operating Hours" />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Open Time */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Open Time
                  </label>
                  <div className="relative">
                    <Input
                      type="text"
                      value={modalData.openTime}
                      readOnly
                      onClick={() => setIsOpenTimeModalOpen(true)}
                      className="cursor-pointer pr-10"
                      placeholder="Select open time"
                    />
                    <button
                      onClick={() => setIsOpenTimeModalOpen(true)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#885ABB] hover:text-[#7748A4]"
                    >
                      <Clock size={18} />
                    </button>
                  </div>
                </div>

                {/* Close Time */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Close Time
                  </label>
                  <div className="relative">
                    <Input
                      type="text"
                      value={modalData.closeTime}
                      readOnly
                      onClick={() => setIsCloseTimeModalOpen(true)}
                      className="cursor-pointer pr-10"
                      placeholder="Select close time"
                    />
                    <button
                      onClick={() => setIsCloseTimeModalOpen(true)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#885ABB] hover:text-[#7748A4]"
                    >
                      <Clock size={18} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Slot Duration */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Slot Duration
                </label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={modalData.slotDuration}
                    onChange={(e) =>
                      onInputChange("slotDuration", e.target.value)
                    }
                    className="flex-1"
                    placeholder="30"
                  />
                  <span className="text-sm text-gray-600 font-medium">Minutes</span>
                </div>
              </div>
            </div>

            {/* Booking Settings Section */}
            <div>
              <SectionHeader title="Booking Settings" />

              {/* Info Box */}
              <div className="mb-4 p-3 bg-purple-50 rounded-lg border border-purple-100">
                <p className="text-xs text-purple-700 flex items-center gap-2">
                  <Info size={14} className="flex-shrink-0" />
                  Set 0 for unlimited bookings
                </p>
              </div>

              <div className="space-y-4">
                {/* Allow multiple bookings */}
                <FormField
                  label="Allow Multiple Bookings"
                  name="allowMultipleBookings"
                  value={modalData.allowMultipleBookings}
                  onChange={onInputChange}
                  type="select"
                  options={[
                    { value: "Yes", label: "Yes" },
                    { value: "No", label: "No" },
                  ]}
                />

                {/* Maximum Number of Booking Allowed */}
                <FormField
                  label="Max Bookings Allowed"
                  name="maxBookingsAllowed"
                  value={modalData.maxBookingsAllowed}
                  onChange={onInputChange}
                  type="text"
                  placeholder="0"
                />

                {/* Maximum Number of Booking Allowed Per Day */}
                <FormField
                  label="Max Bookings Per Day"
                  name="maxBookingsPerDay"
                  value={modalData.maxBookingsPerDay}
                  onChange={onInputChange}
                  type="text"
                  placeholder="0"
                />

                {/* Maximum Number of Booking Allowed Per Slot */}
                <FormField
                  label="Max Bookings Per Slot"
                  name="maxBookingsPerSlot"
                  value={modalData.maxBookingsPerSlot}
                  onChange={onInputChange}
                  type="text"
                  placeholder="0"
                />

                {/* Status */}
                <FormField
                  label="Status"
                  name="status"
                  value={modalData.status}
                  onChange={onInputChange}
                  type="select"
                  options={[
                    { value: "Active", label: "Active" },
                    { value: "Inactive", label: "Inactive" },
                  ]}
                />
              </div>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-xl">
            <Button
              onClick={onClose}
              className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 text-sm font-medium"
            >
              Cancel
            </Button>
            <Button
              onClick={onSubmit}
              className="bg-[#885ABB] hover:bg-[#7748A4] text-white px-6 py-2 text-sm font-medium shadow-sm hover:shadow-md transition-all"
            >
              Save Changes
            </Button>
          </div>
        </div>
      </div>

      {/* Time Picker Modals */}
      <TimePickerModal
        isOpen={isOpenTimeModalOpen}
        onClose={() => setIsOpenTimeModalOpen(false)}
        selectedTime={modalData.openTime}
        onSelectTime={(time) => onInputChange("openTime", time)}
      />
      <TimePickerModal
        isOpen={isCloseTimeModalOpen}
        onClose={() => setIsCloseTimeModalOpen(false)}
        selectedTime={modalData.closeTime}
        onSelectTime={(time) => onInputChange("closeTime", time)}
      />

      {/* Animation Styles */}
      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .animate-slide-up {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default EditDayScheduleModal;
